import { useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { COMP_RAFFLE_TYPE } from 'marketing/components/Competitions/constants'
import Group from 'components/utils/Group'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import Divider from 'LuxKit/Divider'
import TextButton from 'LuxKit/Button/TextButton'
import GeoContext from 'contexts/geoContext'
import { formatDynamicAmount, formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import Caption from 'LuxKit/Typography/Caption'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ClickableLink from 'components/Common/Clickable/ClickableLink'

function isRaffleUserStatus(userStatus: App.MarketingCompetitionUserStatusPromo | App.MarketingCompetitionUserStatusRaffle): userStatus is App.MarketingCompetitionUserStatusRaffle {
  return userStatus.competitionType === COMP_RAFFLE_TYPE
}

const PrizeBox = styled(Group)`
  padding: ${rem(40)} ${rem(20)};
  border-radius: 2px;
  border: 2px solid ${props => props.theme.palette.neutral.default.one};
  max-width: ${rem(380)};

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
  }
`

const PromoBox = styled(Group)`
  padding: ${rem(12)} ${rem(8)};
  width: 100%;
  background: ${props => props.theme.palette.neutral.default.six};
`

const StyledClickableLink = styled(ClickableLink)`
  text-decoration: underline;
  display: inline-block;
`

const CompetitionHeading = styled(Heading)`
  margin: 1rem 0 0 0;
`

interface Props {
  grandPrizeDetails?: App.MarketingGrandPrizeDetail
  announcedTime?: string
  prizeContactDate: string | null
  openModalGrandPrizes: () => void
  competitionUserStatus: App.MarketingCompetitionUserStatusPromo | App.MarketingCompetitionUserStatusRaffle
  promoLink?: string
  currencyCode: string
}

function GrandPrizeTile(props: Props) {
  const {
    grandPrizeDetails,
    announcedTime,
    prizeContactDate,
    openModalGrandPrizes,
    competitionUserStatus,
    promoLink,
    currencyCode,
  } = props

  const intl = useIntl()
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const isRaffle = isRaffleUserStatus(competitionUserStatus)

  const raffleHeading = intl.formatMessage({
    id: 'njnUm-UANz0mh3S67l8o',
    defaultMessage: 'Success, you’re in the running for the grand prize!',
    description: 'Heading shown when user has entered the raffle competition',
  })
  const promoHeading = intl.formatMessage({
    id: '8FtJDiTL7ObgwiBI-t04',
    defaultMessage: 'You have already claimed your\nexclusive discount!',
    description: 'Heading shown when user has already claimed their promo discount',
  })
  const headingText = isRaffle ? raffleHeading : promoHeading

  const discountText = useMemo(() => {
    if (!isRaffle) {
      if (competitionUserStatus.promoType === 'fixed_amount') {
        return `${formatDynamicAmount(competitionUserStatus.discountValue, currentCurrency, currentRegionCode)}`
      } else {
        return `${competitionUserStatus.discountValue}%`
      }
    }
    return ''
  }, [competitionUserStatus, currentCurrency, currentRegionCode, isRaffle])

  const bodyText = isRaffle ? intl.formatMessage(
    {
      id: '8mqpY435ACxSAwTCLABd',
      defaultMessage: 'Grand prize: {prizeName}',
      description: 'Grand prize name display in competition result tile',
    },
    { prizeName: grandPrizeDetails?.offer?.name },
  ) : undefined

  const raffleSubLine1 = intl.formatMessage(
    {
      id: 'PJiAejtpd-08fHNFQ8t5',
      defaultMessage: 'Draw Date: {drawDate}',
      description: 'Draw date display in competition raffle result tile',
    },
    { drawDate: announcedTime },
  )
  const promoSubLine1 = intl.formatMessage(
    {
      id: '04ZNQFpReGM1_liPtlfF',
      defaultMessage: 'Your promo code for {discountText}* off to claim on your next escape has also been',
      description: 'First line of promo code claim message in competition result tile',
    },
    { discountText },
  )
  const subBodyTextLine1 = isRaffle ? raffleSubLine1 : promoSubLine1

  const raffleSubLine2 = prizeContactDate ? intl.formatMessage(
    {
      id: 'YBP_vWe-2VvCL-INCJEB',
      defaultMessage: 'Winner will be contacted via email by {prizeContactDate}',
      description: 'Winner contact date notice in competition raffle result tile',
    },
    { prizeContactDate },
  ) : ''
  const promoSubLine2 = intl.formatMessage({
    id: '8XMhc82HOhFqr1U4HOUc',
    defaultMessage: 'sent to your email.',
    description: 'Second line of promo code claim message in competition result tile',
  })
  const subBodyTextLine2 = isRaffle ? raffleSubLine2 : promoSubLine2

  const minMaxDiscountText = isRaffle ? null : intl.formatMessage(
    {
      id: 'sc_XgEKuDpro5q6BVRuZ',
      defaultMessage: '*Minimum spend {minSpend}. Maximum discount {maxDiscount}.',
      description: 'Minimum spend and maximum discount terms for competition promo code',
    },
    {
      minSpend: formatRoundedAmountWithDollarType(competitionUserStatus.minSpend, currencyCode, currentRegionCode),
      maxDiscount: formatRoundedAmountWithDollarType(competitionUserStatus.maxDiscount, currencyCode, currentRegionCode),
    },
  )

  const toTheDrawLabel = intl.formatMessage({
    id: 'qC836PqFaw3xJh-QrQ9H',
    defaultMessage: 'to the draw!',
    description: 'Subtitle below entry count in competition raffle result tile',
  })
  const discountOffLabel = intl.formatMessage(
    {
      id: 'wFfgm0FGPP3iWed_cmMR',
      defaultMessage: '{discountText} off',
      description: 'Discount amount display in competition promo result tile',
    },
    { discountText },
  )
  const towardsNextEscapeLabel = intl.formatMessage({
    id: 'OZvdtNiXc4i-QSB3R5cH',
    defaultMessage: 'towards your next escape!',
    description: 'Subtitle below discount amount in competition promo result tile',
  })
  const enterPromoLabel = intl.formatMessage({
    id: 'TWSZVvpyJQECrww6UWG4',
    defaultMessage: 'Enter promo code at checkout:',
    description: 'Label above promo code in competition result tile on the marketing page',
  })
  const browseExclusiveOffersLabel = intl.formatMessage({
    id: 'TQbe1wqmBqApfTsIrLb4',
    defaultMessage: 'Feel free to continue browsing our exclusive offers.',
    description: 'Message encouraging users to browse offers after raffle entry',
  })
  const browseOffersLabel = intl.formatMessage({
    id: 'Ih_NesOcYjy8w3yZ4j-T',
    defaultMessage: 'Browse offers',
    description: 'Button text linking to offers page from competition result tile',
  })
  const viewGrandPrizeLabel = intl.formatMessage({
    id: 'avl7lF75xj78gUGFyrUz',
    defaultMessage: 'View full grand prize details',
    description: 'Button text to open grand prize details modal in competition',
  })

  // navigate to homepage if there is no offer link
  const navigateToOffers = promoLink || '/'

  return (
    <Group direction="vertical" gap={20} tabletHorizontalAlign="center">
      <Group direction="vertical" gap={8} horizontalAlign="center">
        <CSSBreakpoint min="tablet">
          <CompetitionHeading variant="heading3" align="center">
            {headingText}
          </CompetitionHeading>
          {bodyText && (
            <BodyText variant="large" align="center" colour="neutral-two">{bodyText}</BodyText>
          )}
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <Heading variant="heading3" align="start">
            {headingText}
          </Heading>
          {bodyText && (
            <BodyText variant="large" align="start" colour="neutral-two">{bodyText}</BodyText>
          )}
        </CSSBreakpoint>
      </Group>
      <PrizeBox direction="vertical" gap={20} horizontalAlign="center">
        <Group direction="vertical" gap={8} horizontalAlign="center">
          <LineGiftIcon size={54} />
          <Group direction="vertical" gap={0} horizontalAlign="center">
            {isRaffle && (
              <>
                <Heading variant="heading2" align="center">
                  <FormattedMessage
                    id="23f1cw7CKep0eyFsLbAo"
                    defaultMessage="{count, plural, one {# entry} other {# entries}}"
                    description="Number of raffle entries won in competition result tile"
                    values={{ count: Number(competitionUserStatus.prizeValue) }}
                  />
                </Heading>
                <BodyText variant="large">
                  {toTheDrawLabel}
                </BodyText>
              </>
            )}
            {!isRaffle && (
              <>
                <Heading variant="heading2" align="center">
                  {discountOffLabel}
                </Heading>
                <BodyText variant="large">
                  {towardsNextEscapeLabel}
                </BodyText>
              </>
            )}
          </Group>
        </Group>
        <Group direction="vertical" gap={8} horizontalAlign="center">
          {(subBodyTextLine1 || subBodyTextLine2) && <div>
            {[subBodyTextLine1, subBodyTextLine2].map((text, index) => text && <BodyText key={`${text}-${index}`} variant="medium" align="center">{text}</BodyText>)}
          </div>}
        </Group>
        <Divider kind="primary" />
        {!isRaffle && (
          <PromoBox direction="vertical" horizontalAlign="center" gap={4}>
            <Caption variant="large">
              {enterPromoLabel}
            </Caption>
            <Heading variant="heading5">
              {competitionUserStatus.codeName}
            </Heading>
          </PromoBox>
        )}
        {minMaxDiscountText && <div>
          <Caption variant="medium" colour="neutral-three" align="center">{minMaxDiscountText}</Caption>
          <Caption variant="medium" colour="neutral-three" align="center">
            <FormattedMessage
              id="OfcFgSHhSvlTOGQCDBy7"
              defaultMessage="<link>Terms and conditions</link> apply"
              description="Terms and conditions link in competition result tile"
              values={{
                link: (chunks) => <StyledClickableLink to="/promotions" target="_blank">{chunks}</StyledClickableLink>,
              }}
            />
          </Caption>
        </div>}
        {isRaffle && (
          <BodyText variant="medium" align="center">
            {browseExclusiveOffersLabel}
          </BodyText>
        )}
        <TextButton kind="primary" to={navigateToOffers} data-url={navigateToOffers} target="_blank" fit="full-width">
          {browseOffersLabel}
        </TextButton>
      </PrizeBox>
      {isRaffle && openModalGrandPrizes && (
        <TextButton kind="tertiary" onClick={openModalGrandPrizes}>
          {viewGrandPrizeLabel}
        </TextButton>
      )}
    </Group>
  )
}

function mapStateToProps(state: App.State) {
  return {
    currencyCode: state.geo.currentCurrency,
  }
}

export default connect(mapStateToProps)(GrandPrizeTile)
